export type ApiResult<T> = T;

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

type ApiRequestOptions = {timeoutMs?: number};

type AdminApiTarget = {origin: string; token: string};
let adminApiTarget: AdminApiTarget | null = null;

/** Used only by the official admin console when it is viewing test data. */
export function setAdminApiTarget(target: AdminApiTarget | null): void {
  if (!target) { adminApiTarget = null; return; }
  const origin = new URL(target.origin).origin;
  if (origin !== 'https://tax-test.xiaostorm.com' || !target.token.trim()) throw new Error('测试环境管理地址无效');
  adminApiTarget = {origin, token: target.token.trim()};
}

function resolveRequest(url: string): {url: string; bridgeToken?: string} {
  if (!adminApiTarget) return {url};
  const parsed = new URL(url, window.location.origin);
  if (parsed.origin !== window.location.origin || !parsed.pathname.startsWith('/api/admin/')) return {url};
  return {url: `${adminApiTarget.origin}${parsed.pathname}${parsed.search}`, bridgeToken: adminApiTarget.token};
}

async function request<T>(url: string, init?: RequestInit, options?: ApiRequestOptions): Promise<ApiResult<T>> {
  const controller = new AbortController();
  const timeoutMs = Math.max(1000, options?.timeoutMs ?? 15000);
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  const target = resolveRequest(url);
  let response: Response;
  try {
    response = await fetch(target.url, {
      credentials: target.bridgeToken ? 'omit' : 'include',
      ...init,
      signal: init?.signal ?? controller.signal,
      headers: {
        ...(init?.body instanceof FormData ? {} : {'Content-Type': 'application/json'}),
        ...(init?.headers ?? {}),
        ...(target.bridgeToken ? {Authorization: `Bearer ${target.bridgeToken}`} : {}),
      },
    });
  } catch (error) {
    if (controller.signal.aborted) throw new Error(`请求超时：${target.url}`);
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'error' in payload
      ? String((payload as {error?: unknown}).error)
      : payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as {message?: unknown}).message)
        : typeof payload === 'string' && payload.trim()
          ? payload
          : `请求失败（${response.status}）`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

export const api = {
  get: <T>(url: string, options?: ApiRequestOptions) => request<T>(url, {cache: 'no-store'}, options),
  post: <T>(url: string, body?: unknown, options?: ApiRequestOptions) => request<T>(url, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
  }, options),
  put: <T>(url: string, body?: unknown) => request<T>(url, {
    method: 'PUT',
    body: JSON.stringify(body ?? {}),
  }),
  patch: <T>(url: string, body?: unknown) => request<T>(url, {
    method: 'PATCH',
    body: JSON.stringify(body ?? {}),
  }),
  delete: <T>(url: string, body?: unknown) => request<T>(url, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
  }),
  upload: async <T>(url: string, body: Blob, mimeType: string): Promise<T> => {
    const response = await fetch(url, {method: 'PUT', credentials: 'include', headers: {'Content-Type': mimeType}, body});
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(payload && typeof payload === 'object' && 'error' in payload ? String(payload.error) : `上传失败（${response.status}）`, response.status, payload);
    return payload as T;
  },
};
